import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Shield,
  Home,
  ScanLine,
  History,
  BarChart3,
  Menu,
  X,
  Sun,
  Moon,
  Laptop,
  LogOut,
  User
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import authService from '@/services/auth'

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home,
    description: 'Overview and analytics'
  },
  {
    name: 'New Scan',
    href: '/start-scan',
    icon: ScanLine,
    description: 'Start security scan'
  },
  {
    name: 'Scan History',
    href: '/history',
    icon: History,
    description: 'View past scans'
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    description: 'Security insights'
  },
]

export function Navigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false)

  const { user, logout } = useAuthStore()
  const { theme, setTheme, sidebarOpen, toggleSidebar } = useUIStore()

  const handleLogout = async () => {
    try {
      await authService.logout()
    } finally {
      logout()
      navigate('/login')
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-50 lg:w-72 lg:bg-white lg:dark:bg-gray-900 lg:border-r lg:border-gray-200 lg:dark:border-gray-800',
          !sidebarOpen && 'lg:w-16'
        )}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto px-6 py-4">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-blue-600" />
            {sidebarOpen && (
              <span className="ml-3 text-xl font-semibold text-gray-900 dark:text-white">
                RiskLens
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="ml-auto"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-2">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-md p-3 text-sm font-semibold leading-6 transition-colors',
                        isActive
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                      title={!sidebarOpen ? item.name : undefined}
                    >
                      <item.icon
                        className={cn(
                          'h-6 w-6 shrink-0',
                          isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-500'
                        )}
                      />
                      {sidebarOpen && (
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{item.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {item.description}
                          </div>
                        </div>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>

          {/* User Menu */}
          <div className="flex flex-col gap-y-2">
            {/* Theme Selector */}
            <div className="relative">
              <Button
                variant="ghost"
                size={sidebarOpen ? 'default' : 'icon'}
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                className="w-full justify-start"
              >
                {theme === 'dark' && <Moon className="h-5 w-5" />}
                {theme === 'light' && <Sun className="h-5 w-5" />}
                {theme === 'system' && <Laptop className="h-5 w-5" />}
                {sidebarOpen && <span className="ml-3">Theme</span>}
              </Button>

              {isThemeMenuOpen && (
                <div className="absolute bottom-full mb-2 left-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
                  <button
                    onClick={() => { setTheme('light'); setIsThemeMenuOpen(false) }}
                    className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Sun className="h-4 w-4 mr-2" />
                    Light
                  </button>
                  <button
                    onClick={() => { setTheme('dark'); setIsThemeMenuOpen(false) }}
                    className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Moon className="h-4 w-4 mr-2" />
                    Dark
                  </button>
                  <button
                    onClick={() => { setTheme('system'); setIsThemeMenuOpen(false) }}
                    className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Laptop className="h-4 w-4 mr-2" />
                    System
                  </button>
                </div>
              )}
            </div>

            {/* User Profile */}
            <div className={cn(
              'flex items-center gap-x-3 p-3 rounded-md',
              'text-gray-700 dark:text-gray-300'
            )}>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                <User className="h-4 w-4" />
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {user?.full_name || user?.username}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <Button
              variant="ghost"
              size={sidebarOpen ? 'default' : 'icon'}
              onClick={handleLogout}
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-5 w-5" />
              {sidebarOpen && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between bg-white dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-blue-600" />
            <span className="ml-3 text-xl font-semibold text-gray-900 dark:text-white">
              RiskLens
            </span>
          </div>

          <div className="flex items-center gap-x-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-gray-900 px-6 py-6 sm:max-w-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Shield className="h-8 w-8 text-blue-600" />
                  <span className="ml-3 text-xl font-semibold text-gray-900 dark:text-white">
                    RiskLens
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>

              <nav className="space-y-2">
                {navigationItems.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        'group flex items-center gap-x-3 rounded-md p-3 text-sm font-semibold leading-6',
                        isActive
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                      )}
                    >
                      <item.icon className="h-6 w-6 shrink-0" />
                      <div>
                        <div>{item.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </nav>

              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-x-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {user?.full_name || user?.username}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email}
                    </p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
